import { Component, h, Prop, State, Method, Element, Event, EventEmitter } from '@stencil/core';
import { UserService } from '../aafp-authentication-wrapper/userService';
import { getClassListString } from '../component-utils/stencilUtils';

@Component({
  tag: 'aafp-buy-now-button',
  shadow: false,
})
export class AafpBuyNowButton {
  @Prop() sku!: string;
  @Prop({ mutable: true, reflect: true }) cartBtnText: string = 'Add to Cart';
  @Prop({ mutable: true, reflect: true }) saveBtnText: string = 'Save for Later';
  /**
   * Optional - Input name of price rule to override price
   */
  @Prop({ mutable: true }) priceRuleOverride: string = '';
  /**
   * Makes Add to Cart button use secondary styling
   */
  @Prop() altStyle: boolean = false;
  /**
   * Only shows Add to Cart button
   */
  @Prop() simpleButton: boolean = false;
  @Prop() ariaLabel: string | null;

  /**
   * Status properties are primariy set externally from cartFunction.js
   */
  @Prop({ mutable: true }) statusCartAdded: boolean = false;
  @Prop({ mutable: true }) statusSaveAdded: boolean = false;
  @Prop({ mutable: true }) statusCartError: boolean = false;
  @Prop({ mutable: true }) statusSaveError: boolean = false;
  @Prop({ mutable: true }) statusCartLoading: boolean = false;
  @Prop({ mutable: true }) showAlreadyOwned: boolean = false;
  @Prop() dataEditMode: boolean = false;

  @Prop() btnStateLoaded: boolean = false;
  @State() itemDetailsLoaded: boolean = false;

  @Element() buttonEl: HTMLElement;
  @State() isAuthenticated: boolean = true;
  userService: any;

  @State() priceDisplayName = '';
  @State() price = '$';
  @State() overridenPrice = '';
  @State() accessUrl = '';
  @Prop() badSku = false;

  @Event() add2CartClicked: EventEmitter<void>;
  @Event() save4LaterClicked: EventEmitter<void>;

  /**
   * Waits for both the button state from cartFunctions.js and the item details
   * to populate before removing the ghost loader overlay
   */
  get dataLoaded(): boolean {
    return this.btnStateLoaded && this.itemDetailsLoaded;
  }

  get priceName(): string {
    if (
      this.priceDisplayName === 'DEFAULT' ||
      this.priceDisplayName === 'Default Price' ||
      this.priceDisplayName === ''
    )
      return 'Price';
    else return this.priceDisplayName;
  }

  componentWillLoad() {
    this.userService = new UserService('https://www.aafp.org/jsparse/AAFPUser.js');
    if (!this.dataEditMode) this.getAuthenticationStatus();

    if (this.isAuthenticated) this.getItemDetails();
  }

  @Method()
  async getAuthenticationStatus() {
    return this.userService
      .isAuthenticated()
      .then((isAuthenticated: boolean) => {
        this.isAuthenticated = isAuthenticated;
      })
      .catch(() => {});
  }

  handleAdd2Cart_click = () => {
    if (!this.badSku) this.add2CartClicked.emit();
  };

  handleSave4Later_click = () => {
    if (!this.badSku) this.save4LaterClicked.emit();
  };

  async getItemDetails() {
    const data = new URLSearchParams({
      sku: this.sku,
      priceName: this.priceRuleOverride,
    });

    await fetch('https://www.aafp.org/api/v2/ecommerce/store/item?' + data, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (res.status !== 200) {
          // Sets error state if sku provided is invalid
          // or if api call was unsuccessful
          this.badSku = true;
          this.simpleButton = true;
          this.statusCartAdded = true;
          this.itemDetailsLoaded = true;
          return false;
        }
        return res.json();
      })
      .then((response) => {
        (window as any).itemDetails = (window as any).itemDetails || {};
        (window as any).itemDetails[this.sku] = response;

        this.showAlreadyOwned = response.HasPurchased && !response.IsAllowMultiplePurchases;
        this.accessUrl = response.ResourceUrl;

        this.price += response.Price.Amount;
        if (response.Price.Amount !== response.DefaultPrice.Amount) {
          this.overridenPrice = <span class="override-price">${response.DefaultPrice.Amount}</span>;
        }
        this.priceDisplayName = response.Price.DisplayName;

        if (response.Price.Name !== this.priceRuleOverride) {
          this.priceRuleOverride = '';
        }

        this.itemDetailsLoaded = true;
      })
      .catch(() => {});
  }

  getHeadingDetails() {
    if (this.isAuthenticated) {
      if (this.showAlreadyOwned) {
        return <h3 class="adl-h3">You've Already Got This!</h3>;
      } else {
        return (
          <div>
            <h3 class="adl-h3">Your Price</h3>
            <div class="buy-now-btn--details">
              <p>{this.priceName}</p>
              <p>
                {this.price}
                {this.overridenPrice}
              </p>
            </div>
          </div>
        );
      }
    } else {
      const logInUrl = `https://www.aafp.org/cgi-bin/lg.pl?redirect=${window.location.href}`;
      const createActUrl = `https://www.aafp.org/account/home/new?loginState=${encodeURIComponent(`RelayState=${window.location.href}`)}`;

      return (
        <div>
          <h3 class="adl-h3">
            <a href={logInUrl}>Log in</a>
            &nbsp;or <a href={createActUrl}>create an account</a>
            &nbsp;to see your price
          </h3>
          <p class="buy-now-btn--details">*Membership not required</p>
        </div>
      );
    }
  }

  getSave4LaterBtn() {
    return (
      <div class="buy-now-btn--flex">
        <div
          class={getClassListString(
            { 'adl-hide': !this.statusSaveAdded && !this.statusSaveError },
            'buy-now-btn--status',
          )}
        >
          {this.statusSaveError ? (
            <span>
              <i class="material-icons color-error-red">highlight_off</i>
              Error, try again
            </span>
          ) : (
            <span>
              <i class="material-icons color-affirmation-green">check</i>
              Saved for later
            </span>
          )}
        </div>
        <button onClick={this.handleSave4Later_click} class="aafp-button button-secondary">
          {this.statusSaveAdded ? (
            'View Saved'
          ) : (
            <span>
              <i class="material-icons">add</i> {this.saveBtnText}
            </span>
          )}
        </button>
      </div>
    );
  }

  getAdd2CartBtn() {
    let cartBtnText = this.cartBtnText;
    if (this.statusCartLoading) cartBtnText = 'Adding to Cart';
    else if (this.statusCartAdded) cartBtnText = 'View Cart';

    return (
      <div class="buy-now-btn--flex">
        <div
          class={getClassListString(
            { 'adl-hide': !this.statusCartAdded && !this.statusCartError && !this.badSku },
            'buy-now-btn--status',
          )}
        >
          {this.statusCartError || this.badSku ? (
            <span>
              <i class="material-icons color-error-red">highlight_off</i>
              {this.badSku ? (
                <span class="buynow-bad-sku">
                  Sorry, we are unable to add the item to your cart at this time.
                  <br />
                  <br />
                  Please contact our Member Resource Center at 1-800-274-2237 for assistance.
                </span>
              ) : (
                'Error, try again'
              )}
            </span>
          ) : (
            <span>
              <i class="material-icons color-affirmation-green">check</i>
              Added to cart
            </span>
          )}
        </div>
        <button
          onClick={this.handleAdd2Cart_click}
          aria-label={this.ariaLabel}
          class={getClassListString(
            {
              'button-primary': !this.altStyle,
              'button-secondary': this.altStyle,
              'button-loading': this.statusCartLoading,
              'adl-hide': this.badSku,
            },
            'aafp-button',
          )}
        >
          {this.statusCartAdded ? 'View Cart' : cartBtnText}
        </button>
      </div>
    );
  }

  getAccessNowBtn() {
    return (
      <div>
        {this.accessUrl !== '' ? (
          <a
            href={this.accessUrl}
            target="_blank"
            class={getClassListString(
              { 'button-primary': !this.altStyle, 'button-secondary': this.altStyle },
              'aafp-button adl-space__sm__m-b-sm button-full-width',
            )}
          >
            Access Now
          </a>
        ) : (
          ''
        )}
        <div class={getClassListString({ 'adl-text-align--center': this.accessUrl !== '' })}>
          <a href="https://www.aafp.org/account/profile/home#Purchases" class="view-purchases-link">
            View All Purchases
          </a>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div
        class={getClassListString(
          {
            loading: !this.dataLoaded,
          },
          'buy-now-btn--container',
        )}
      >
        {!this.dataLoaded && !this.dataEditMode ? (
          <div class="buy-now-ghost-loader">
            <div class="ghost-txt1"></div>
            <div class="ghost-txt2"></div>
            <div class="ghost-btn1"></div>
            <div class="ghost-btn2"></div>
          </div>
        ) : (
          ''
        )}
        {!this.simpleButton ? this.getHeadingDetails() : ''}
        {!this.showAlreadyOwned ? this.getAdd2CartBtn() : ''}
        {!this.showAlreadyOwned && !this.simpleButton && !this.statusCartAdded ? this.getSave4LaterBtn() : ''}
        {this.showAlreadyOwned ? this.getAccessNowBtn() : ''}
      </div>
    );
  }
}
